import { Badge } from "@mantine/core";
import type { FC } from "react";
import { getOrganizerStatusBadgeProps } from "../../../pages/TaxProClientList/components/ClientListTable/utils/badgeUtils";

interface OrganizerStatusBadgeProps {
  organizerStatus: string | null;
  /** Optional size override, defaults to "lg" */
  size?: string;
  /** Optional icon size override, defaults to 16 */
  iconSize?: number;
  /** Optional onClick handler */
  onClick?: (e: React.MouseEvent) => void;
  /** Optional className for additional styling */
  className?: string;
}

/**
 * Common OrganizerStatusBadge component that displays tax organizer status with consistent styling
 * Uses Mantine Badge component with icons and colors from badgeUtils
 * :param organizerStatus: The organizer status string to display (nullable)
 * :param size: Optional size for the badge (default: "lg")
 * :param iconSize: Optional icon size (default: 16)
 * :param onClick: Optional click handler
 * :param className: Optional additional CSS classes
 * :return: Mantine Badge with organizer status styling and icon
 */
export const OrganizerStatusBadge: FC<OrganizerStatusBadgeProps> = ({
  organizerStatus,
  size = "lg",
  iconSize = 16,
  onClick,
  className,
}) => {
  const badgeConfig = getOrganizerStatusBadgeProps(organizerStatus);

  // Clone the icon element with the custom size if icon exists
  const iconWithSize = badgeConfig.icon
    ? typeof badgeConfig.icon === "object" && "props" in badgeConfig.icon
      ? {
          ...badgeConfig.icon,
          props: {
            ...badgeConfig.icon.props,
            size: iconSize,
          },
        }
      : badgeConfig.icon
    : null;

  return (
    <Badge
      color={badgeConfig.color}
      variant="filled"
      size={size}
      radius="xl"
      leftSection={iconWithSize}
      onClick={onClick}
      className={className}
      styles={{
        root: {
          backgroundColor: badgeConfig.color,
          color: "#ffffff",
          fontWeight: 600,
          textTransform: "none",
          paddingLeft: "12px",
          paddingRight: "16px",
          paddingTop: "8px",
          paddingBottom: "8px",
          fontSize: "14px",
          height: "auto",
          cursor: onClick ? "pointer" : "default",
        },
        section: {
          marginRight: "6px",
        },
      }}
    >
      {badgeConfig.text}
    </Badge>
  );
};
